import { useEffect, useState } from "react";
import { BASE_URL } from "../apiConfig";
// Import additional chart types
import {
  BarChart,
  Bar,
  LineChart,
  Line,

  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";

const InsightBlock = ({
  description,
  summary,
}: {
  description?: string;
  summary?: string;
}) => (
  <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
    <p className="text-sm text-slate-600">{description}</p>
    {summary ? (
      <div className="mt-3 border-l-4 border-slate-400 pl-3">
        <p className="text-xs font-medium text-slate-700">Key insight</p>
        <p className="text-sm text-slate-700">{summary}</p>
      </div>
    ) : null}
  </div>
);

const Card = ({
  title,
  subtitle,
  children,
  headerRight,
}: {
  title: string;
  subtitle?: string;
  children: React.ReactNode;
  headerRight?: React.ReactNode;
}) => (
  <div className="rounded-2xl border border-slate-200 bg-white shadow-sm">
    <div className="flex items-center justify-between border-b border-slate-100 px-5 py-4">
      <div>
        <h3 className="font-semibold text-slate-900">{title}</h3>
        {subtitle ? (
          <p className="mt-0.5 text-xs text-slate-500">{subtitle}</p>
        ) : null}
      </div>
      {headerRight}
    </div>
    <div className="p-5">{children}</div>
  </div>
);

type InsightData = {
  description?: string;
  summary?: string;
  data?: any[];
};

export default function SalesTab() {
  const [skuStoreMismatch, setSkuStoreMismatch] = useState<InsightData | null>(null);
  const [holidaySpikes, setHolidaySpikes] = useState<InsightData | null>(null);
  const [topSkusOverall, setTopSkusOverall] = useState<InsightData | null>(null);
  const [recommendations, setRecommendations] = useState<any[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    async function fetchSalesInsights() {
      setLoading(true);
      setError(null);
      try {
        const res = await fetch(`${BASE_URL}/sales-insights`);
        if (!res.ok) throw new Error(`HTTP error! status: ${res.status}`);
        const data = await res.json();

        setSkuStoreMismatch(data.sku_store_mismatch);
        setHolidaySpikes(data.holiday_spikes);
        setTopSkusOverall(data.top_skus_overall);
        setRecommendations(data.recommendations);
      } catch (err: any) {
        setError(err.message || "Unknown error");
      } finally {
        setLoading(false);
      }
    }
    fetchSalesInsights();
  }, []);

  const formatSkuMismatchData = (d: any) =>
    d?.data?.map((item: any) => ({
      name: item.sku_id,
      online: item.online_qty,
      offline: item.offline_qty_est,
    })) ?? [];

  const formatHolidaySpikesData = (d: any) =>
    d?.data?.map((item: any) => ({
      name: item.sku_id,
      holiday: item.holiday_avg_qty,
      baseline: item.baseline_daily_qty,
    })) ?? [];

  const formatTopSkusData = (d: any) =>
    d?.data?.map((item: any) => ({
      name: item.sku_id,
      quantity: item.qty,
      margin: item.est_margin_per_unit,
    })) ?? [];

  if (loading) {
    return (
      <div className="p-6 md:p-8">
        <div className="h-6 w-48 animate-pulse rounded bg-slate-200" />
        <div className="mt-6 h-64 w-full animate-pulse rounded-2xl bg-slate-200" />
      </div>
    );
  }
  if (error) return <div className="p-6 md:p-8 text-red-600">Error: {error}</div>;

  return (
    <div className="p-6 md:p-8 space-y-8">
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-bold text-slate-900">Sales Insights</h1>
      </div>

      {/* SKU Store Mismatch */}
      <Card
        title="SKU Store Mismatch"
        subtitle="Compare online vs. offline quantities to spot stocking gaps"
      >
        <div className="mb-5">
          <InsightBlock
            description={skuStoreMismatch?.description}
            summary={skuStoreMismatch?.summary}
          />
        </div>
        <div className="h-[300px] w-full">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart
              layout="vertical"
              data={formatSkuMismatchData(skuStoreMismatch)}
              margin={{ top: 10, right: 30, left: 50, bottom: 0 }}
            >
              <CartesianGrid stroke="#F3F4F6" strokeDasharray="3 3" />
              <XAxis type="number" tick={{ fill: "#6B7280", fontSize: 12 }} />
              <YAxis dataKey="name" type="category" tick={{ fill: "#6B7280", fontSize: 12 }} />
              <Tooltip />
              <Legend />
              <Bar dataKey="online" name="Online" fill="#111827" radius={[0, 4, 4, 0]} />
              <Bar dataKey="offline" name="Offline" fill="#D1D5DB" radius={[0, 4, 4, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </Card>

      {/* Two-column: Holiday Spikes / Top SKUs */}
      <div className="grid grid-cols-1 gap-6 xl:grid-cols-2">
        <Card title="Holiday Sales Spikes" subtitle="Holiday vs. baseline averages">
          <div className="mb-5">
            <InsightBlock
              description={holidaySpikes?.description}
              summary={holidaySpikes?.summary}
            />
          </div>
          <div className="h-[300px] w-full">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart
                data={formatHolidaySpikesData(holidaySpikes)}
                margin={{ top: 10, right: 10, left: 0, bottom: 0 }}
              >
                <CartesianGrid stroke="#F3F4F6" strokeDasharray="3 3" />
                <XAxis dataKey="name" tick={{ fill: "#6B7280", fontSize: 12 }} />
                <YAxis tick={{ fill: "#6B7280", fontSize: 12 }} />
                <Tooltip />
                <Legend />
                <Line 
                  type="monotone" 
                  dataKey="holiday" 
                  name="Holiday" 
                  stroke="#111827" 
                  strokeWidth={2} 
                  dot={{ fill: "#111827" }} 
                />
                <Line 
                  type="monotone" 
                  dataKey="baseline" 
                  name="Baseline" 
                  stroke="#D1D5DB" 
                  strokeWidth={2} 
                  dot={{ fill: "#D1D5DB" }} 
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </Card>

        <Card title="Top Performing SKUs" subtitle="Quantity vs. margin per unit">
          <div className="mb-5">
            <InsightBlock
              description={topSkusOverall?.description}
              summary={topSkusOverall?.summary}
            />
          </div>
          <div className="h-[300px] w-full">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                data={formatTopSkusData(topSkusOverall)}
                margin={{ top: 10, right: 10, left: 0, bottom: 0 }}
              >
                <CartesianGrid stroke="#F3F4F6" strokeDasharray="3 3" />
                <XAxis dataKey="name" tick={{ fill: "#6B7280", fontSize: 12 }} />
                <YAxis tick={{ fill: "#6B7280", fontSize: 12 }} />
                <Tooltip />
                <Legend />
                <Bar dataKey="quantity" name="Quantity" fill="#111827" radius={[4, 4, 0, 0]} />
                <Bar dataKey="margin" name="Margin/Unit" fill="#9CA3AF" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Card>
      </div>

      {/* Recommendations */}
      <Card title="Action Items & Recommendations">
        <div className="space-y-4">
          {(recommendations ?? []).length === 0 ? (
            <p className="text-sm text-slate-500">No recommendations available.</p>
          ) : (
            recommendations?.map((rec: any, i: number) => (
              <div key={i} className="rounded-xl border border-slate-200 bg-slate-50 p-4">
                <h3 className="font-medium text-slate-900">Area: {rec.insight}</h3>
                <p className="mt-1 text-slate-600">{rec.recommendation}</p>
                {rec.sample_skus && (
                  <div className="mt-2 text-sm text-slate-600">
                    <span className="font-medium text-slate-500">Sample SKUs: </span>
                    {rec.sample_skus.join(", ")}
                  </div>
                )}
              </div>
            ))
          )}
        </div>
      </Card>
    </div>
  );
}